import React, { useState } from "react";

import Select from "../../../../components/input/select/Select";
import TextField from "../../../../components/input/textfield/TextField";
import TextArea from "../../../../components/input/textarea/TextArea";

import {
  FormSection,
  FormRow,
} from "../../../../components/layout/forms/Index";

import TalentModal from "./TalentModal";
import { clean, getApiError } from "../utils/talentUtils";

import "./TalentForms.css";

const visibilityOptions = [
  { label: "Public", value: "Public" },
  { label: "Private", value: "Private" },
];

const initialFields = (initial) => ({
  talentSportId: initial?.talentSportId ?? "",
  title: initial?.title || "",
  externalUrl: initial?.externalUrl || "",
  description: initial?.description || "",
  visibility: initial?.visibility || "Public",
});

const validVideoUrl = (value) => {
  try {
    const url = new URL(value);

    if (url.protocol !== "https:") return false;

    const host = url.hostname.toLowerCase();

    return [
      "youtube.com",
      "www.youtube.com",
      "m.youtube.com",
      "youtu.be",
      "www.youtu.be",
      "vimeo.com",
      "www.vimeo.com",
    ].includes(host);
  } catch {
    return false;
  }
};

export default function TalentMediaForm({
  initial,
  sports = [],
  saving = false,
  onSave,
  onClose,
}) {
  const [fields, setFields] = useState(() => initialFields(initial));
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");

  const sportOptions = [
    { label: "General / Not specified", value: "" },
    ...sports.map((sport) => ({
      label: sport.sport,
      value: String(sport.sportId),
    })),
  ];

  const change = ({ target }) => {
    const { name, value } = target;

    setFields((previous) => ({
      ...previous,
      [name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: "",
    }));

    setError("");
  };

  const save = async () => {
    if (saving) return;

    const next = {};

    const title = clean(fields.title);
    const externalUrl = clean(fields.externalUrl);

    if (!title) {
      next.title = "Media title is required.";
    }

    if (!validVideoUrl(externalUrl)) {
      next.externalUrl = "Enter a valid YouTube or Vimeo URL.";
    }

    if (
      fields.talentSportId !== "" &&
      !sports.some(
        (sport) => String(sport.sportId) === String(fields.talentSportId),
      )
    ) {
      next.talentSportId = "Select a valid sport.";
    }

    if (!visibilityOptions.some(({ value }) => value === fields.visibility)) {
      next.visibility = "Select a valid visibility.";
    }

    setErrors(next);

    if (Object.keys(next).length) return;

    const payload = {
      talentSportId:
        fields.talentSportId === "" ? null : Number(fields.talentSportId),
      title,
      externalUrl,
      description: clean(fields.description) || null,
      visibility: fields.visibility,
    };

    try {
      setError("");
      await onSave(payload);
    } catch (err) {
      setError(getApiError(err));
    }
  };

  return (
    <TalentModal
      title={initial ? "Edit Media" : "Add Media"}
      description={
        initial
          ? "Update your highlight details and external video link."
          : "Add a YouTube or Vimeo highlight to your Talent ID."
      }
      busy={saving}
      onClose={onClose}
      onSave={save}
    >
      {error && (
        <p className="user-talent__error" role="alert">
          {error}
        </p>
      )}

      <FormSection>
        <TextField
          label="Media Title"
          name="title"
          value={fields.title}
          onChange={change}
          placeholder="e.g. 2026 Season Highlights"
          maxLength={150}
          required
        />

        <Select
          label="Sport"
          name="talentSportId"
          value={String(fields.talentSportId)}
          items={sportOptions}
          onChange={change}
          error={errors.talentSportId}
        />

        <TextField
          label="Video URL"
          name="externalUrl"
          type="url"
          value={fields.externalUrl}
          onChange={change}
          placeholder="https://www.youtube.com/watch?v=..."
          maxLength={2048}
          required
        />

        <Select
          label="Visibility"
          name="visibility"
          value={fields.visibility}
          items={visibilityOptions}
          onChange={change}
          error={errors.visibility}
          required
        />

        <TextArea
          label="Description"
          name="description"
          value={fields.description}
          onChange={change}
          placeholder="Describe the highlights..."
          maxLength={10000}
          maxHeight={140}
        />

        <p className="talent-form__note">
          YouTube and Vimeo links are supported. Your video remains hosted on
          the original platform.
        </p>
      </FormSection>
    </TalentModal>
  );
}
